import { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { Space } from '@goatapp/web-content-components';

import { initialSpaceItemsPlacement } from 'gift-cards/enums/constants';
import { useGiftCardsDispatch, useGiftCardsState } from 'gift-cards/hooks/useGiftCards';
import { GiftCardsActions } from 'gift-cards/state/giftCardsReducer';
import { IGiftCardSpace } from 'gift-cards/types';
import { transformSubsections, isGif } from 'gift-cards/utils/transformSpaces';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { requestService } from 'shared/utils/services/requestService';
import { variableFontRegular } from 'styles/theme/text';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { t } from 'localization/utils/t';

const TP = 'giftCards.components.pdp.GiftCardSpace';

interface IGiftCardSpaceProps {
  isActive?: boolean;
  space: IGiftCardSpace;
  height?: number;
  defaultActiveObject?: string;
  hasMobileEditMode?: boolean;
  selectedObjectToFront?: boolean;
}

export const GiftCardSpace = ({
  isActive = false,
  space,
  height = 440,
  defaultActiveObject = '',
  hasMobileEditMode = false,
  selectedObjectToFront = false,
}: IGiftCardSpaceProps) => {
  const { isMobile } = useWindowSize();
  const { spaceSections, spaceInMobileEditMode } = useGiftCardsState();
  const isMounted = useIsMounted();
  const currentSpaceSection = spaceSections[space.id];
  const noSubsections = { ...currentSpaceSection, subsections: [] };
  const width = currentSpaceSection.aspectRatio * height;
  const canLeaveSpaceBlank = ['GOAT Selects', 'Most Wanted'].includes(space.name);

  const dispatch = useGiftCardsDispatch();

  const handleKeepBlank = (e) => {
    e.stopPropagation();
    // remove items from canvas https://github.com/goatapp/web-content-components/blob/main/src/components/Space/Space.tsx#L95
    dispatch({
      type: GiftCardsActions.setCurrentSpaceSection,
      payload: {
        spaceSection: { ...currentSpaceSection, subsections: [], isBlank: true },
        id: space.id,
      },
    });

    sendTrackingEvent('GC_LEAVE_BLANK_TAP', {
      gc_theme: space.name,
    });
  };

  const handleRandomizeItems = async (e) => {
    e.stopPropagation();
    // remove items from canvas https://github.com/goatapp/web-content-components/blob/main/src/components/Space/Space.tsx#L95
    // need to remove items first before being able to add new ones
    if (!currentSpaceSection?.isBlank) {
      dispatch({
        type: GiftCardsActions.setCurrentSpaceSection,
        payload: {
          spaceSection: { ...currentSpaceSection, subsections: [], isBlank: false },
          id: space.id,
        },
      });
    }

    try {
      const items = await requestService({
        url: `/web-api/v1/spaces/items/random?groupId=${String(space.id)}&quantity=${Number(4)}`,
        method: 'get',
      });

      const mappedItems = items.dataList.map((groupItem, index) => {
        return {
          id: `${groupItem.groupId as string}-${index as number}`,
          spaceId: groupItem.groupId,
          placement: initialSpaceItemsPlacement[index],
          productTemplate: groupItem.productTemplate,
        };
      });

      const subsections = transformSubsections(mappedItems);

      dispatch({
        type: GiftCardsActions.setCurrentSpaceSection,
        payload: {
          spaceSection: {
            ...currentSpaceSection,
            subsections,
            isBlank: false,
            itemsList: items.dataList,
          },
          id: space.id,
        },
      });

      sendTrackingEvent('GC_RANDOM_TAP', {
        gc_theme: space.name,
        product_template_slugs: items.dataList.map((item) => item.productTemplate.slug).join(','),
      });
    } catch (error) {
      bugsnag(error, { context: 'Error fetching gift card space random items' });
    }
  };

  const handleUpdate = (data) => {
    const { idOfModified, ...spaceSection } = data;

    dispatch({
      type: GiftCardsActions.setCurrentSpaceSection,
      payload: { spaceSection, id: space.id },
    });

    sendTrackingEvent('GC_ITEM_TRANSFORM', {
      product_template_slug: space.items.find((item) => item.id === idOfModified)?.productTemplate
        ?.slug,
      gift_card_space_id: space.id,
    });
  };

  const handleItemSelected = (data) => {
    if (!data || !selectedObjectToFront) return;

    const { selectedId, ...spaceSection } = data;

    if (hasMobileEditMode && !spaceInMobileEditMode) {
      dispatch({
        type: GiftCardsActions.setSpaceInMobileEditMode,
        payload: { spaceId: space.id, itemId: selectedId },
      });
      return;
    }
    dispatch({
      type: GiftCardsActions.setCurrentSpaceSection,
      payload: { spaceSection, id: space.id },
    });

    sendTrackingEvent('GC_ITEM_TAP', {
      product_template_slug: space.items.find((item) => item.id === selectedId)?.productTemplate
        ?.slug,
    });
  };

  const handleBlankSpaceMobileEditMode = (e) => {
    e.stopPropagation();

    if (currentSpaceSection.subsections.length > 0 || !isMobile) return;
    dispatch({
      type: GiftCardsActions.setSpaceInMobileEditMode,
      payload: { spaceId: space.id, itemId: 'reset' },
    });
  };

  useEffect(() => {
    if (isActive && isMounted()) {
      dispatch({ type: GiftCardsActions.setSelectedSpaceId, payload: space.id });
    }
  }, [isActive, space]);

  return (
    <GiftCardSpace.Wrapper
      width={width}
      isActive={isActive}
      onClick={handleBlankSpaceMobileEditMode}
    >
      {isActive && (!isMobile || spaceInMobileEditMode) && (
        <GiftCardSpace.ControlWrapper $spaceInMobileEditMode={spaceInMobileEditMode}>
          <GiftCardSpace.Controls data-qa="gift_card_random_link" onClick={handleRandomizeItems}>
            {currentSpaceSection?.isBlank
              ? t(`${TP}.generate`, 'Generate')
              : t(`${TP}.random`, 'Random')}
          </GiftCardSpace.Controls>
          {!currentSpaceSection?.isBlank && canLeaveSpaceBlank && (
            <GiftCardSpace.Controls data-qa="gift_card_leave_blank_link" onClick={handleKeepBlank}>
              {t(`${TP}.leaveBlank`, 'Leave Blank')}
            </GiftCardSpace.Controls>
          )}
        </GiftCardSpace.ControlWrapper>
      )}
      <GiftCardSpace.SpaceWrapper
        isActive={isActive}
        bgImage={currentSpaceSection.bgImage}
        height={height}
        width={width}
        widthPercentage={(isMobile ? 14700 : 17100) / width}
        heightPercentage={(isMobile ? 22000 : 25600) / height}
      >
        {isActive && (
          <Space
            canEdit={isActive}
            space={isActive ? currentSpaceSection : noSubsections}
            maxHeight={height + 1}
            onSpaceUpdate={handleUpdate}
            onItemSelected={handleItemSelected}
            animation={{ name: 'fadeIn', duration: 500 }}
            objectControls={{
              mt: false,
              mb: false,
              ml: false,
              mr: false,
              cornerSize: 6,
              borderColor: 'white',
              cornerColor: 'white',
              cornerStrokeColor: 'white',
              transparentCorners: false,
            }}
            selectedObjectToFront={selectedObjectToFront}
            defaultActiveObject={defaultActiveObject}
          />
        )}
      </GiftCardSpace.SpaceWrapper>
      {!spaceInMobileEditMode && (
        <GiftCardSpace.Text>
          <GiftCardSpace.Name isActive={isActive} $spaceInMobileEditMode={spaceInMobileEditMode}>
            {space.name}
          </GiftCardSpace.Name>
          {isActive && (
            <GiftCardSpace.Edit data-qa="gift_card_edit_button">
              {isMobile
                ? t(`${TP}.editMobile`, 'Click item to edit')
                : t(`${TP}.editDesktop`, 'Click item to scale, move or rotate')}
            </GiftCardSpace.Edit>
          )}
        </GiftCardSpace.Text>
      )}
    </GiftCardSpace.Wrapper>
  );
};

GiftCardSpace.Wrapper = styled.div<{
  width: number;
  isActive: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ width, isActive }) =>
    isActive
      ? css`
          width: ${width}px;
        `
      : css`
          width: 171px;

          @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
            width: 147px;
          }
        `};
`;

GiftCardSpace.ControlWrapper = styled.div<{ $spaceInMobileEditMode: string }>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 16px;

  ${({ $spaceInMobileEditMode }) =>
    !$spaceInMobileEditMode &&
    css`
      padding-top: 40px;
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${({ $spaceInMobileEditMode }) =>
      !$spaceInMobileEditMode &&
      css`
        padding-top: 104px;
      `}
  }
`;

GiftCardSpace.Controls = styled.div`
  text-decoration: underline;
  cursor: pointer;
  height: 11.5px;
  ${variableFontRegular({ size: '11.5px', height: '11.5px' })};

  animation: dissolve 0.5s ease-out;

  @keyframes dissolve {
    0% {
      opacity: 0;
    }
    75% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '11.5px' })};
  }
`;

GiftCardSpace.SpaceWrapper = styled.div<{
  isActive: boolean;
  bgImage: string;
  height: number;
  width: number;
  widthPercentage: number;
  heightPercentage: number;
}>`
  cursor: pointer;
  overflow: hidden;
  max-height: 750px;

  ${({ bgImage, isActive }) =>
    bgImage &&
    (!isActive || (isActive && !isGif(bgImage))) &&
    css`
      background-image: url(${bgImage});
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
    `}

  ${({ isActive, height, width, widthPercentage, heightPercentage }) =>
    !isActive
      ? css`
          height: 256px;
          width: 171px;

          @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
            height: 220px;
            width: 147px;
          }

          transition: all 0.35s ease-out;
        `
      : css`
          height: ${height}px;
          width: ${width}px;

          @keyframes scale {
            from {
              transform: scale(${widthPercentage}%, ${heightPercentage}%);
              transform-origin: bottom;
            }
            to {
              transform: scale(100%, 100%);
              transform-origin: center bottom;
            }
          }

          animation: scale 0.35s ease-out;
        `}
`;

GiftCardSpace.Text = styled.div`
  display: inline;
  padding-top: 24px;
  width: 100%;
`;

GiftCardSpace.Name = styled.div<{ isActive: boolean; $spaceInMobileEditMode: string }>`
  position: relative;
  text-align: center;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  ${({ isActive, $spaceInMobileEditMode }) =>
    isActive &&
    !$spaceInMobileEditMode &&
    css`
      animation: nameSwitch 1.6s ease-in-out 0.5s;

      @keyframes nameSwitch {
        0% {
          opacity: 1;
        }
        19% {
          opacity: 0;
        }
        81% {
          opacity: 0;
        }
        100% {
          opacity: 1;
        }
      }
    `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;

GiftCardSpace.Edit = styled.div`
  position: relative;
  top: -14.5px;
  margin-bottom: -14.5px;
  text-align: center;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};
  opacity: 0;

  animation: instructionsSwitch 1.6s ease-in-out 0.5s;

  @keyframes instructionsSwitch {
    0% {
      opacity: 0;
    }
    19% {
      opacity: 1;
    }
    81% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    ${variableFontRegular({ size: '11px', height: '14px' })};
  }
`;
